import axiosClient from '../axiosClient';
import {
  CreateTaskRequest,
  UpdateTaskRequest,
  GetTasksResponse,
  GetTaskResponse,
  CreateTaskResponse,
  UpdateTaskResponse,
  DeleteTaskResponse,
  MoveTaskRequest,
  MoveTaskResponse,
} from '../types/TaskTypes';

// Helper function to normalize task response
const normalizeTask = (task: any): any => ({
  ...task,
  boardId: String(task.boardId || task.board_id || ''),
  sprintId: task.sprintId || task.sprint_id ? String(task.sprintId || task.sprint_id) : undefined,
  epicId: task.epicId || task.epic_id ? String(task.epicId || task.epic_id) : undefined,
  assigneeId: task.assigneeId || task.assignee_id ? String(task.assigneeId || task.assignee_id) : undefined,
  reporterId: task.reporterId || task.reporter_id ? String(task.reporterId || task.reporter_id) : undefined,
  createdAt: task.createdAt || task.created_at || new Date().toISOString(),
  updatedAt: task.updatedAt || task.updated_at || new Date().toISOString(),
});

export const tasksApi = {
  // Get all tasks for a specific board with optional server-side filters
  getTasks: async (
    boardId: string,
    filters?: {
      status?: string;
      assigneeId?: string;
      assignee?: string;
      label?: string;
      sprintId?: string;
    }
  ): Promise<GetTasksResponse> => {
    try {
      const queryParams = new URLSearchParams({ boardId });
      if (filters?.status) queryParams.append('status', filters.status);
      if (filters?.assigneeId) queryParams.append('assigneeId', filters.assigneeId);
      if (filters?.assignee) queryParams.append('assignee', filters.assignee);
      if (filters?.label) queryParams.append('label', filters.label);
      if (filters?.sprintId) queryParams.append('sprintId', filters.sprintId);

      const response = await axiosClient.get(`/tasks?${queryParams.toString()}`);
      const tasks = Array.isArray(response.data) ? response.data : response.data.tasks || [];
      const normalizedTasks = tasks.map(normalizeTask);
      return { tasks: normalizedTasks, total: normalizedTasks.length };
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      throw error;
    }
  },

  // Get all tasks assigned to a specific user (across all boards)
  getUserTasks: async (assigneeId: string): Promise<GetTasksResponse> => {
    try {
      const queryParams = new URLSearchParams({ assigneeId });
      const response = await axiosClient.get(`/tasks?${queryParams.toString()}`);
      const tasks = Array.isArray(response.data) ? response.data : response.data.tasks || [];
      const normalizedTasks = tasks.map(normalizeTask);
      return { tasks: normalizedTasks, total: normalizedTasks.length };
    } catch (error) {
      console.error('Failed to fetch user tasks:', error);
      throw error;
    }
  },

  // Get backlog tasks for a board (tasks with no sprint_id)
  getBacklog: async (
    boardId: string,
    filters?: { status?: string; assigneeId?: string; label?: string }
  ): Promise<GetTasksResponse> => {
    try {
      const queryParams = new URLSearchParams();
      if (filters?.status) queryParams.append('status', filters.status);
      if (filters?.assigneeId) queryParams.append('assigneeId', filters.assigneeId);
      if (filters?.label) queryParams.append('label', filters.label);

      const queryString = queryParams.toString();
      const url = `/boards/${boardId}/backlog${queryString ? `?${queryString}` : ''}`;
      const response = await axiosClient.get(url);
      const tasks = Array.isArray(response.data) ? response.data : response.data.tasks || [];
      const normalizedTasks = tasks.map(normalizeTask);
      return { tasks: normalizedTasks, total: normalizedTasks.length };
    } catch (error) {
      console.error('Failed to fetch backlog:', error);
      throw error;
    }
  },

  // Get a specific task by ID
  getTask: async (taskId: string): Promise<GetTaskResponse> => {
    try {
      const response = await axiosClient.get(`/tasks/${taskId}`);
      const task = response.data.task || response.data;
      return { task: normalizeTask(task) };
    } catch (error) {
      console.error('Failed to fetch task:', error);
      throw error;
    }
  },

  // Create a new task
  createTask: async (data: CreateTaskRequest): Promise<CreateTaskResponse> => {
    try {
      const response = await axiosClient.post('/tasks', data);
      const task = response.data.task || response.data;
      return { task: normalizeTask(task) };
    } catch (error) {
      console.error('Failed to create task:', error);
      throw error;
    }
  },

  // Update an existing task
  updateTask: async (
    taskId: string,
    data: UpdateTaskRequest
  ): Promise<UpdateTaskResponse> => {
    try {
      const payload: any = { ...data };
      if (payload.sprintId !== undefined && payload.sprintId !== null) {
        payload.sprintId = Number(payload.sprintId);
      }
      if (payload.boardId !== undefined && payload.boardId !== null) {
        payload.boardId = Number(payload.boardId);
      }
      if (payload.epicId !== undefined && payload.epicId !== null) {
        payload.epicId = Number(payload.epicId);
      }
      if (payload.assigneeId !== undefined && payload.assigneeId !== null) {
        payload.assigneeId = Number(payload.assigneeId);
      }
      if (payload.estimatedHours !== undefined && payload.estimatedHours !== null) {
        payload.estimatedHours = Number(payload.estimatedHours);
      }
      if (payload.actualHours !== undefined && payload.actualHours !== null) {
        payload.actualHours = Number(payload.actualHours);
      }
      if (payload.storyPoints !== undefined && payload.storyPoints !== null) {
        payload.storyPoints = Number(payload.storyPoints);
      }
      const response = await axiosClient.patch(`/tasks/${taskId}`, payload);
      const task = response.data.task || response.data;
      return { task: normalizeTask(task) };
    } catch (error) {
      console.error('Failed to update task:', error);
      throw error;
    }
  },

  // Delete a task
  deleteTask: async (taskId: string): Promise<DeleteTaskResponse> => {
    try {
      const response = await axiosClient.delete(`/tasks/${taskId}`);
      return response.data || { success: true, message: 'Task deleted successfully' };
    } catch (error) {
      console.error('Failed to delete task:', error);
      throw error;
    }
  },

  // Bulk update tasks (for drag and drop)
  bulkUpdateTasks: async (updates: Array<{ id: string; updates: UpdateTaskRequest }>): Promise<any> => {
    try {
      const response = await axiosClient.patch('/tasks/bulk', { updates });
      const tasks = Array.isArray(response.data) ? response.data : response.data.tasks || [];
      return tasks.map(normalizeTask);
    } catch (error) {
      console.error('Failed to bulk update tasks:', error);
      throw error;
    }
  },

  // Move a task (status/position) with optimistic concurrency control
  // Backend: PATCH /api/tasks/:id/move
  // Returns 409 if expectedVersion is stale — caller should retry with fresh data
  moveTask: async (taskId: string, data: MoveTaskRequest): Promise<MoveTaskResponse> => {
    try {
      const response = await axiosClient.patch(`/tasks/${taskId}/move`, data);
      return response.data;
    } catch (error) {
      console.error('Failed to move task:', error);
      throw error;
    }
  },
};
